import React from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTableColumns,
  faRightLeft,
  faColumns,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons"
import MenuButton, { MenuButtonItem } from "shared/ui/MenuButton/MenuButton.component"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"

/**
 * Layout controls: a "Layout ▾" menu and the full-screen button.
 *
 * The menu holds what adjusts the workspace:
 *   1. Show/hide the variables (DataTree) panel (ticked while shown).
 *   2. Console+Output stacked or side by side (the active one ticked).
 *   3. Output first, i.e. the two panes swapped (ticked while swapped).
 *
 * Full screen stays a button of its own, since it is the one used most and
 * Esc leaves it again.
 */
const QueryConsoleLayoutControls: React.FC = () => {
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const dataTreeVisible = useQueryConsoleLayoutStore((s) => s.dataTreeVisible)
  const consoleOutputDirection = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputDirection
  )
  const consoleOutputOrder = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputOrder
  )
  const isMaximized = useQueryConsoleLayoutStore((s) => s.isMaximized)
  const toggleDataTree = useQueryConsoleLayoutStore((s) => s.toggleDataTree)
  const toggleDirection = useQueryConsoleLayoutStore((s) => s.toggleDirection)
  const toggleOrder = useQueryConsoleLayoutStore((s) => s.toggleOrder)
  const toggleMaximized = useQueryConsoleLayoutStore((s) => s.toggleMaximized)

  const isHorizontal = consoleOutputDirection === "horizontal"
  const isOutputFirst = consoleOutputOrder === "output-first"
  // Direction and order apply wherever there is an editor/output (or
  // builder/output) split. Transform and Saved Queries are single-pane
  // workspaces with no second pane, so those items are hidden there.
  const hideSplit = viewMode !== "builder" && viewMode !== "console"

  const items: MenuButtonItem[] = [
    {
      key: "variables",
      label: "Variables panel",
      icon: <FontAwesomeIcon icon={faColumns} />,
      checked: dataTreeVisible,
      onClick: toggleDataTree,
    },
    { type: "divider", key: "split-divider" },
    {
      key: "stacked",
      label: "Stacked",
      icon: (
        <FontAwesomeIcon
          icon={faTableColumns}
          style={{ transform: "rotate(90deg)" }}
        />
      ),
      checked: !isHorizontal,
      hidden: hideSplit,
      onClick: () => isHorizontal && toggleDirection(),
    },
    {
      key: "side-by-side",
      label: "Side by side",
      icon: <FontAwesomeIcon icon={faTableColumns} />,
      checked: isHorizontal,
      hidden: hideSplit,
      onClick: () => !isHorizontal && toggleDirection(),
    },
    { type: "divider", key: "order-divider" },
    {
      key: "output-first",
      label: "Output first",
      icon: <FontAwesomeIcon icon={faRightLeft} />,
      checked: isOutputFirst,
      hidden: hideSplit,
      onClick: toggleOrder,
    },
  ]

  return (
    <>
      <MenuButton label="Layout" items={items} />
      <Tooltip
        title={isMaximized ? "Exit full screen (Esc)" : "Expand to full screen"}
        placement="bottom"
      >
        <button
          type="button"
          className="dq-btn dq-btn--icon"
          aria-label={isMaximized ? "Exit full screen" : "Enter full screen"}
          aria-pressed={isMaximized}
          data-active={isMaximized}
          onClick={toggleMaximized}
        >
          <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
        </button>
      </Tooltip>
    </>
  )
}

export default QueryConsoleLayoutControls
